import { ContentDetailType, ContentItemPreviewSnippet, ContentType, UrlParser } from "@authory-packages/core";
import { ManualImportPathTaken } from "../UniversalAddContentTracking";
import { getContentCopyLabel, getContentData } from "../../../utils/article-text-utils";
import { VerticalSpacing } from "../../VerticalSpacing";
import { InlineLink } from "../../ReusableStyledComponents/InlineLink";

export const getAddContentTextAreaPlaceholder = (targetContentType: ManualImportPathTaken) => {
    switch (targetContentType) {
        case ManualImportPathTaken.PodcastEpisode:
            return `Example:

https://podcasts.apple.com/us/podcast/biometrics-green-aaron/id1011668648
https://open.spotify.com/episode/7IZrV1j9qGn9Zql1ZXmhi6 
https://www.iheart.com/podcast/87-24-7-news-the-latest-20697776/episode/`;
        case ManualImportPathTaken.YoutubeOther:
            return `Example:

https://www.youtube.com/watch?v=IC1IjHuyQ0w
https://www.youtube.com/watch?v=p_hu2YXamTw
https://youtu.be/p_hu2YXamTw?si=Ib4RW9051rXqFhrW`;
        case ManualImportPathTaken.VideosOther:
            return `Example:

https://www.cbsnews.com/video/storm-surge-devastates-small-coastal-community/
https://www.theguardian.com/football/video/2023/aug/30/swedish-third-tier-team`;
        case ManualImportPathTaken.SocialMedia:
            return `Example:

https://www.linkedin.com/posts/dannelken_how-funny-up-your-writin-by-dan-nelken-ugcPost-7101938907818205184-N7hp/
https://www.instagram.com/p/Cu3B-wpuVoq/
https://www.tiktok.com/@psg/video/7266368808661552417`;
        case ManualImportPathTaken.PDFs:
            return `Example:
 
https://www.leru.org/files/What-are-Universities-for-Full-paper.pdf
https://eprints.whiterose.ac.uk/143833/3/The%20concept%20of%20a%20university%2C%20final%20version.pdf`;
        case ManualImportPathTaken.Other:
            return `Example:
 
https://www.slideshare.net/Inspiro/time-management-productivity-best-practices
https://pitch.com/presentations/Product-Led-Growth-The-Rise-Of-The-User-2ftN17289rie4M8fSf0uF4HH`;
        case ManualImportPathTaken.Book:
            return `Example:

https://www.amazon.com/Celebrate-Greatest-Festivals-around-World/dp/3961713073/
https://www.barnesandnoble.com/w/james-percival-everett/1143678734?ean=9780385550369`;
        default:
            return `Example:

https://www.nytimes.com/2023/03/movie-theaters-ticket-prices.html
https://www.theatlantic.com/2023/04/arnold-schwarzenegger-speech/
https://thegannet.co/journal/the-digest/hot-sauce-gannet-food-writing/`;
    }
};

export const getAddContentDescription = (targetContentType: ManualImportPathTaken, url?: string, onJumpToPDF?: () => void) => {
    let domain = "";

    if (url) {
        try {
            domain = UrlParser.extractDomain(url) || "";
        } catch {}
    }

    switch (targetContentType) {
        case ManualImportPathTaken.PodcastEpisode:
            return "Paste the URLs of the podcast episodes you'd like to add to your account. Be sure to start a new line for each new URL (like in the example below).";
        case ManualImportPathTaken.YoutubeOther:
            return "Paste the URLs of the YouTube videos you'd like to add to your account. Be sure to start a new line for each new URL (like in the example below).";
        case ManualImportPathTaken.VideosOther:
            return "Paste the URLs of the  videos you'd like to add to your account. Be sure to start a new line for each new URL (like in the example below).";
        case ManualImportPathTaken.SocialMedia:
            return "Paste the URLs of the posts you'd like to add to your account. Be sure to start a new line for each new link (like in the example below).";
        case ManualImportPathTaken.PDFs:
            return "Paste the URLs of the PDFs you'd like to add to your account. Be sure to start a new line for each new link (like in the example below).";
        case ManualImportPathTaken.Other:
            return "Paste the URLs of the content you'd like to add to your account below. Be sure to start a new line for each new link (like in the example below).";
        case ManualImportPathTaken.Book:
            return "Paste the URLs of the books you'd like to add to your account below. Be sure to start a new line for each new link (like in the example below).";
        case ManualImportPathTaken.AddSourceNonBylinedContent:
            return (
                <>
                    If the articles that you want to import don't carry your byline, then our system can't find them automatically as it can't "know"
                    which articles are by you.
                    <VerticalSpacing top={12} as="div">
                        However, you can copy&paste the URLs of any articles you'd like to add below and they'll be imported to your account right
                        away, no matter what the byline is.
                    </VerticalSpacing>
                </>
            );
        case ManualImportPathTaken.AddSourceSomethingElse:
            return (
                <>
                    Have you've checked that the example URL of an article that you entered is correct? And that you've entered all the
                    bylines/aliases that you use?
                    <VerticalSpacing top={12} as="div">
                        If so, and the automated import is still not working, then there is a technical issue {domain ? `with ${domain}` : ""} that's
                        preventing us from finding and importing your content. Sorry about that!
                    </VerticalSpacing>
                    <VerticalSpacing top={12} as="div">
                        You can still import your articles by copy&pasting the respective URLs below:
                    </VerticalSpacing>
                </>
            );
        default:
            return (
                <>
                    Paste the URLs of the articles you'd like to add to your account. Be sure to start a new line for each new URL (like in the
                    example below). Alternatively, you can also <InlineLink onClick={onJumpToPDF}>import articles from PDFs</InlineLink>.
                </>
            );
    }
};

export const getAddContentInputLabel = (targetContentType: ManualImportPathTaken) => {
    switch (targetContentType) {
        case ManualImportPathTaken.PodcastEpisode:
            return "Podcast episode URLs";
        case ManualImportPathTaken.YoutubeOther:
            return "YouTube video URLs";
        case ManualImportPathTaken.VideosOther:
            return "Video URLs";
        case ManualImportPathTaken.SocialMedia:
            return "Paste the URLs of your social media posts";
        case ManualImportPathTaken.Book:
            return "Book URLs";
        case ManualImportPathTaken.Other:
            return "URLs";
        default:
            return `URLs of your articles`;
    }
};

export const getContentProcessLabel = (
    type: ContentType,
    hasSources: boolean,
    contentFound: boolean,
    contentDetailType: ContentDetailType | undefined
) => {
    let sentence = "";

    if (contentFound) {
        const isUploadAsset =
            type === ContentType.PdfFile ||
            type === ContentType.Image ||
            type === ContentType.Email ||
            contentDetailType === ContentDetailType.CustomAudio ||
            contentDetailType === ContentDetailType.CustomVideo;

        if (isUploadAsset) {
            sentence = `Good news, your ${getContentCopyLabel(type)} has been uploaded.`;
        } else {
            sentence = `Good news, your ${getContentCopyLabel(type)} has been found!`;
        }

        return `${sentence} ${
            hasSources
                ? `You can edit the preview below, and confirm to which source the ${getContentCopyLabel(type)} will be be added.`
                : "You can edit the preview and name the source below."
        }`;
    } else {
        return hasSources
            ? `You can edit the preview of your ${getContentCopyLabel(type)} below, and confirm to which source it will be added.`
            : `You can edit the preview of your ${getContentCopyLabel(type)} and name the source below.`;
    }
};

/**
 * The snippet endpoint answers even when it couldn't read the page, so a response object on its own proves nothing.
 * It counts as failed when there is no response, when it reports an error status, or when every field we'd
 * prefill the preview with came back empty.
 */
export const isSnippetImportFailed = (snippetRes: ContentItemPreviewSnippet | undefined) => {
    if (!snippetRes) return true;
    if (snippetRes.status === "error") return true;

    return !snippetRes.title && !snippetRes.description && !snippetRes.thumbnailImage;
};

export const removeFileExtension = (filename: string) => filename.replace(/\.[^.]+$/, "");

export const getImportContentCopyLabel = (type: ContentType) =>
    getContentData(type)
        .label.toLowerCase()
        .replace("pdf", "PDF")
        .replace("podcast", "podcast episode")
        .replace("email", "email screenshot")
        .replace("website", "website screenshot");

export const getTargetContentTypeBasedonManualImportFlow = (importPathTaken: ManualImportPathTaken) => {
    switch (importPathTaken) {
        case ManualImportPathTaken.WebsiteScreenshot:
            return ContentType.Website;
        case ManualImportPathTaken.PDFs:
            return ContentType.PdfFile;
        case ManualImportPathTaken.PodcastEpisode:
            return ContentType.PodcastItem;
        case ManualImportPathTaken.VideosOther:
        case ManualImportPathTaken.YoutubeOther:
            return ContentType.Video;
        case ManualImportPathTaken.SocialMedia:
            return ContentType.TextPost;
        case ManualImportPathTaken.Image:
            return ContentType.Image;
        case ManualImportPathTaken.Book:
            return ContentType.Book;
        default:
            return ContentType.Article;
    }
};
